"use client";

import { useState, useEffect, useCallback } from "react";
import { SidebarTrigger } from "@/shadcnComponents/ui/sidebar";
import { Button } from "@/shadcnComponents/ui/button";
import { Input } from "@/shadcnComponents/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcnComponents/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcnComponents/ui/table";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, Eye } from "lucide-react";
import { Badge } from "@/shadcnComponents/ui/badge";
import { CampaignDetailsDialog } from "../DeveloperComponents/campaign-details-dialog";
import { useNavigate } from "react-router-dom";
import { isoDateToDateString } from "@/Helpers/times-ago";
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

type Campaign = {
  id: number;
  brandName: string;
  campaignId: string;
  campaignName: string;
  activatedAt: string;
  influencersShortlisted: number;
  confirmedInfluencers: number;
  declinedRequests: number;
  declinedCount: number;
  campaignType: "performance" | "fixed";
  expectedLive: string;
  pocName: string;
  pocPhone: string;
  pocEmail: string;
  productInfo: {
    websiteLink: string;
    brief: string;
    resources: string;
  };
};

// Mock data for the campaigns
// const campaigns: Campaign[] = [
//   {
//     id: 1,
//     brandName: "TechGiant",
//     campaignId: "TECH001",
//     campaignName: "Summer Tech Fest",
//     activatedAt: "2023-05-15",
//     influencersShortlisted: 50,
//     confirmedInfluencers: 30,
//     declinedRequests: 15,
//     declinedCount: 5,
//     campaignType: "performance",
//     expectedLive: "2023-07-01",
//     pocName: "John Doe",
//     pocPhone: "+1 (555) 123-4567",
//     pocEmail: "john.doe@techgiant.com",
//     productInfo: {
//       websiteLink: "https://techgiant.com/summer-fest",
//       brief: "Promote our latest gadgets for the summer season",
//       resources: "https://techgiant.com/influencer-resources",
//     },
//   },
//   {
//     id: 2,
//     brandName: "FashionForward",
//     campaignId: "FASH002",
//     campaignName: "Autumn Collection Launch",
//     activatedAt: "2023-06-01",
//     influencersShortlisted: 75,
//     confirmedInfluencers: 45,
//     declinedRequests: 20,
//     declinedCount: 10,
//     campaignType: "fixed",
//     expectedLive: "2023-08-15",
//     pocName: "Jane Smith",
//     pocPhone: "+1 (555) 987-6543",
//     pocEmail: "jane.smith@fashionforward.com",
//     productInfo: {
//       websiteLink: "https://fashionforward.com/autumn-collection",
//       brief: "Showcase our new autumn styles across social media platforms",
//       resources: "https://fashionforward.com/brand-guidelines",
//     },
//   },
//   {
//     id: 3,
//     brandName: "EcoFriendly",
//     campaignId: "ECO003",
//     campaignName: "Green Living Challenge",
//     activatedAt: "2023-06-10",
//     influencersShortlisted: 100,
//     confirmedInfluencers: 80,
//     declinedRequests: 0,
//     declinedCount: 0,
//     campaignType: "performance",
//     expectedLive: "2023-07-20",
//     pocName: "Alex Green",
//     pocPhone: "+1 (555) 246-8135",
//     pocEmail: "alex.green@ecofriendly.com",
//     productInfo: {
//       websiteLink: "https://ecofriendly.com/green-challenge",
//       brief: "Encourage followers to adopt eco-friendly practices",
//       resources: "https://ecofriendly.com/campaign-materials",
//     },
//   },
// ];

export default function AdminAllCampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<string | null>(
    null
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState(campaigns);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );

  const getCampaigns = useCallback(async () => {
    try {
      const response = await fetch(`${host}/developer/brand/activeCollabs`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Access-Control-Allow-Credentials": "true",
          jwtToken: localStorage.getItem("jwtTokenD"),
        },
      });
      const json = await response.json();
      if (json.success) {
        setCampaigns(json?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const columns: ColumnDef<Campaign>[] = [
    {
      accessorKey: "brandName",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Brand Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
    },
    {
      accessorKey: "campaignId",
      header: "Campaign ID",
    },
    {
      accessorKey: "campaignName",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Campaign Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return <div className="text-wrap">{row.getValue("campaignName")}</div>;
      },
    },
    {
      accessorKey: "activatedAt",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Activated At
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="text-left">
            {row.getValue("activatedAt")
              ? isoDateToDateString(row.getValue("activatedAt"))
              : "N/A"}
          </div>
        );
      },
    },
    {
      accessorKey: "campaignType",
      header: "Campaign Type",
      cell: ({ row }) => {
        const campaignType = row.getValue("campaignType") as string;
        return (
          <Badge
            variant="outline"
            className={
              campaignType === "performance" ? "bg-blue-100" : "bg-green-100"
            }
          >
            {campaignType}
          </Badge>
        );
      },
    },
    {
      accessorKey: "influencersShortlisted",
      header: "Influencers Shortlisted",
      cell: ({ row }) => {
        return (
          <div className="text-right">
            {row.getValue("influencersShortlisted")}
          </div>
        );
      },
    },
    {
      accessorKey: "confirmedInfluencers",
      header: "Confirmed Influencers",
      cell: ({ row }) => {
        return (
          <div className="text-right">
            {row.getValue("confirmedInfluencers")}
          </div>
        );
      },
    },
    {
      accessorKey: "declinedRequests",
      header: "Declined Requests",
      cell: ({ row }) => {
        return (
          <div className="text-right">{row.getValue("declinedRequests")}</div>
        );
      },
    },
    {
      accessorKey: "declinedCount",
      header: "Declined Count",
      cell: ({ row }) => {
        return (
          <div className="text-right">{row.getValue("declinedCount")}</div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const campaign = row.original;
        return (
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleReview(campaign.id)}
            >
              Review
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleViewDetails(campaign)}
            >
              <Eye className="h-4 w-4" />
              <span className="sr-only">View Details</span>
            </Button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: filteredCampaigns,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
  };

  const handleReview = (campaignId: number) => {
    navigate(`/developer/admin/campaign/${campaignId}`);
  };

  useEffect(() => {
    getCampaigns();
  }, [getCampaigns]);
  useEffect(() => {
    const filtered = campaigns.filter((campaign) => {
      const matchesSearch =
        campaign.brandName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.campaignName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCampaignType = campaignTypeFilter
        ? campaign.campaignType === campaignTypeFilter ||
          campaignTypeFilter === "all"
        : true;
      return matchesSearch && matchesCampaignType;
    });
    setFilteredCampaigns(filtered);
  }, [searchTerm, campaignTypeFilter, campaigns]);

  return (
    <>
      <div className="flex flex-col h-screen">
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-6">
          <SidebarTrigger />
          <h1 className="text-2xl font-bold">All Campaigns</h1>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <Input
              placeholder="Search by brand or campaign name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
            <Select onValueChange={(value) => setCampaignTypeFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Campaign Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="performance">Performance</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2">
              {campaignTypeFilter && campaignTypeFilter !== "all" && (
                <Badge variant="secondary" className="px-2 py-1">
                  Type: {campaignTypeFilter}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-2 h-auto p-0 text-xs"
                    onClick={() => setCampaignTypeFilter(null)}
                  >
                    ×
                  </Button>
                </Badge>
              )}
            </div>
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </main>
      </div>

      <CampaignDetailsDialog
        campaign={selectedCampaign}
        open={!!selectedCampaign}
        onOpenChange={(open) => !open && setSelectedCampaign(null)}
      />
    </>
  );
}
